import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import * as uuid from 'uuid';

import { environment } from 'src/environments/environment';
import { ENaarGoExceptionType, ENaarGoResponseResult, ErrorNaarGoResponse, IErrorNaarGoResponse, INaarGoRequest } from './naar-api-client';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '../storage.service';
import { LoadingService } from '../loading.service';

export enum EBackendServiceRequestContentType {
	ApplicationJson = "application/json",
	ApplicationFormUrlEncoded = "application/x-www-form-urlencoded",
	MultipartFormData = "multipart/form-data",
	TextPlain = "text/plain",
	TextHtml = "text/html"
}

@Injectable({
	providedIn: 'root',
})
export class BackendService {

	private _authToken: string;
	public get authToken(): string {
		return this._authToken;
	}
	public setAuthToken(token: string) {
		this._authToken = token;
	}

	constructor(
		private http: HttpClient,
		private alert: AlertController,
		private translate: TranslateService,
		private storage: StorageService,
		private loading: LoadingService,
	) {
	}

	public async post<T>(url: string, data: INaarGoRequest, displayError: boolean = true): Promise<T> {
		const endpoint = environment.servicesEndpointRoot;
		try {
			const headers = new HttpHeaders({
				'Content-Type': EBackendServiceRequestContentType.ApplicationJson,
				'Authorization': `Bearer ${this.authToken}`
			});

			const uniqueId = uuid.v4();
			data.uniqueID = uniqueId;

			this.checkNetworkConnection();
			return await lastValueFrom(this.http.post<T>(endpoint + url, data, { headers: headers }));
		} catch (error) {
			if (displayError) this.displayError(error);
			throw error;
		}
	}

	public async postFormData<T>(url: string, formData: FormData): Promise<T> {
		const endpoint = environment.servicesEndpointRoot;
		try {
			const headers = new HttpHeaders({
				'Authorization': `Bearer ${this.authToken}`
			});

			const uniqueId = uuid.v4();
			formData.append('uniqueID', uniqueId);

			this.checkNetworkConnection();
			return await lastValueFrom(this.http.post<T>(endpoint + url, formData, { headers: headers }));
		} catch (error) {
			this.displayError(error);
			throw error;
		}
	}

	public async get<T>(url: string): Promise<T> {
		const endpoint = environment.servicesEndpointRoot;
		try {
			const headers = new HttpHeaders({
				'Content-Type': EBackendServiceRequestContentType.ApplicationJson,
				'Authorization': `Bearer ${this.authToken}`
			});

			this.checkNetworkConnection();
			return await lastValueFrom(this.http.get<T>(endpoint + url, { headers: headers }));
		} catch (error) {
			this.displayError(error);
			throw error;
		}
	}

	private checkNetworkConnection() {
		if (!this.storage.isNetworkConnected$()) {
			throw { error: new ErrorNaarGoResponse({ result: ENaarGoResponseResult.Error, exceptionType: ENaarGoExceptionType.NetworkError }) };
		}
	}

	private async displayError(error: any) {
		await this.loading.stop();
		console.error(error);

		let label = 'ERRORS.Generic';

		if (error.error && (error.error as IErrorNaarGoResponse).exceptionType) {
			label = 'ERRORS.' + (error.error as IErrorNaarGoResponse).exceptionType;
		}

		const alert = await this.alert.create({
			header: this.translate.instant('ALERT.HEADER.ERROR'),
			message: this.translate.instant(label),
			buttons: [this.translate.instant('ALERT.BUTTONS.OK')],
		});
		await alert.present();
	}
}
